export type DeployRefreshBuildMeta = {
  app: string;
  schemaVersion: string;
  buildId: string;
  gitSha?: string;
  builtAt?: string;
  version?: string;
};

export type CreateDeployRefreshBuildMetaInput = {
  app: string;
  builtAt: string;
  gitSha: string;
  version?: string;
  buildId?: string;
  schemaVersion?: string;
};

export type FetchDeployRefreshBuildMetaOptions = {
  metaPath?: string;
  origin?: string;
  fetchImpl?: typeof fetch;
};

export type DeployRefreshGuardEnabledOptions = {
  currentBuildId: string;
  production: boolean;
  storageKey?: string;
};

export const DEPLOY_REFRESH_META_PATH = '/build-meta.json';
export const DEFAULT_DEPLOY_REFRESH_POLL_INTERVAL_MS = 60_000;
export const DEFAULT_DEPLOY_REFRESH_SAFE_TO_RELOAD_EVENT = 'triedge:deploy-refresh-safe-to-reload';
export const DEFAULT_DEPLOY_REFRESH_STORAGE_KEY = 'triedge.deployRefreshGuard.enabled';

export const normalizeDeployRefreshBuildId = (value: unknown): string => (
  typeof value === 'string' ? value.trim() : ''
);

const normalizeString = (value: unknown, fallback: string): string => {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized || fallback;
};

const hashBuildIdentity = (value: string): string => {
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const mask = 0xffffffffffffffffn;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= BigInt(value.charCodeAt(index));
    hash = (hash * prime) & mask;
  }
  return hash.toString(16).padStart(16, '0');
};

export const createDeployRefreshBuildMeta = ({
  app,
  builtAt,
  gitSha,
  version = '0.0.0',
  buildId,
  schemaVersion = '1.0',
}: CreateDeployRefreshBuildMetaInput): DeployRefreshBuildMeta => {
  const normalizedApp = normalizeString(app, 'app');
  const normalizedBuiltAt = normalizeString(builtAt, 'unknown');
  const normalizedGitSha = normalizeString(gitSha, 'unknown');
  const normalizedVersion = normalizeString(version, '0.0.0');
  const normalizedBuildId = normalizeDeployRefreshBuildId(buildId)
    || hashBuildIdentity(JSON.stringify([
      normalizedApp,
      normalizedGitSha,
      normalizedBuiltAt,
      normalizedVersion,
    ]));

  return {
    app: normalizedApp,
    schemaVersion: normalizeString(schemaVersion, '1.0'),
    buildId: normalizedBuildId,
    gitSha: normalizedGitSha,
    builtAt: normalizedBuiltAt,
    version: normalizedVersion,
  };
};

export const renderDeployRefreshBuildMetaJson = (meta: DeployRefreshBuildMeta): string => (
  `${JSON.stringify(meta, null, 2)}\n`
);

export const parseDeployRefreshBuildMeta = (value: unknown): DeployRefreshBuildMeta | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const buildId = normalizeDeployRefreshBuildId(record.buildId);
  if (!buildId) return null;

  return {
    app: normalizeString(record.app, 'app'),
    schemaVersion: normalizeString(record.schemaVersion, '1.0'),
    buildId,
    gitSha: normalizeString(record.gitSha, ''),
    builtAt: normalizeString(record.builtAt, ''),
    version: normalizeString(record.version, '') || undefined,
  };
};

export const hasDifferentDeployRefreshBuildId = (
  serverMeta: DeployRefreshBuildMeta | null,
  currentBuildId: string,
): boolean => {
  const serverBuildId = normalizeDeployRefreshBuildId(serverMeta?.buildId);
  const clientBuildId = normalizeDeployRefreshBuildId(currentBuildId);
  return Boolean(serverBuildId && clientBuildId && serverBuildId !== clientBuildId);
};

const resolveMetaUrl = (metaPath: string, origin?: string): URL | null => {
  const baseOrigin = origin || (typeof window === 'undefined' ? '' : window.location.origin);
  if (!baseOrigin) return null;
  return new URL(metaPath, baseOrigin);
};

export const fetchDeployRefreshBuildMeta = async ({
  metaPath = DEPLOY_REFRESH_META_PATH,
  origin,
  fetchImpl = typeof fetch === 'undefined' ? undefined : fetch,
}: FetchDeployRefreshBuildMetaOptions = {}): Promise<DeployRefreshBuildMeta | null> => {
  const metaUrl = resolveMetaUrl(metaPath, origin);
  if (!metaUrl || !fetchImpl) return null;

  const response = await fetchImpl(metaUrl, {
    cache: 'no-store',
    headers: {
      'cache-control': 'no-cache',
      pragma: 'no-cache',
    },
  });
  if (!response.ok) return null;
  return parseDeployRefreshBuildMeta(await response.json());
};

export const readDeployRefreshStorageOverride = (
  storageKey = DEFAULT_DEPLOY_REFRESH_STORAGE_KEY,
): boolean | null => {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(storageKey);
    if (value === '1' || value === 'true') return true;
    if (value === '0' || value === 'false') return false;
  } catch {
    return null;
  }
  return null;
};

export const isDeployRefreshGuardEnabled = ({
  currentBuildId,
  production,
  storageKey = DEFAULT_DEPLOY_REFRESH_STORAGE_KEY,
}: DeployRefreshGuardEnabledOptions): boolean => {
  const override = readDeployRefreshStorageOverride(storageKey);
  if (override !== null) return override;
  return Boolean(production && normalizeDeployRefreshBuildId(currentBuildId) !== 'dev');
};
